import React, {useCallback, useMemo, useState} from 'react';
import type {FieldError, PartDate} from 'schemaDefinition/types';
import {evaluateFieldRequired} from 'schemaDefinition/functions';
import {toFieldError} from 'schemaDefinition/functions/validators';
import {validateSingleDateValue} from 'schemaDefinition/functions/validators/validateDate';
import {isNullish, valueAsString} from 'services/utils';
import {DatePickerField} from 'components/fields/DatePickerField';
import type {BaseFieldProps} from './types';
import {useShowFieldErrors} from '../../hooks';

type Props = BaseFieldProps & {
  part: PartDate;
};

export const DatePicker: React.FC<Props> = ({
  part,
  value,
  setFieldValue,
  valuePath,
  label,
  mode,
  showWhenReadonlyAndEmpty,
  layout,
  localScope,
  globalScope,
  relatedScope,
}) => {
  const [visited, setVisited] = useState(false);
  const handleSetVisited = useCallback(() => setVisited(true), []);
  const showFieldErrors = useShowFieldErrors(visited);

  const handleValueChange = useCallback(
    (date: string | null | undefined) => {
      const newVal = isNullish(date) ? null : date;
      setFieldValue(valuePath, newVal);
    },
    [valuePath, setFieldValue],
  );

  const required = useMemo(() => {
    return evaluateFieldRequired(
      part.required,
      valuePath,
      localScope,
      globalScope,
      relatedScope,
      value,
    );
  }, [globalScope, part.required, relatedScope, localScope, value, valuePath]);

  const error = useMemo((): FieldError => {
    if (showFieldErrors) {
      const valid = validateSingleDateValue(part, {
        value,
        valuePath,
        localScope,
        globalScope,
        relatedScope,
      }).valid;
      return toFieldError(valid);
    }
    return false;
  }, [
    globalScope,
    part,
    relatedScope,
    localScope,
    showFieldErrors,
    value,
    valuePath,
  ]);

  const date = valueAsString(value);
  return (
    <DatePickerField
      name={valuePath}
      label={label}
      value={date}
      readonly={mode === 'read-only'}
      showWhenReadonlyAndEmpty={showWhenReadonlyAndEmpty}
      required={required}
      error={error}
      layout={layout}
      onChange={handleValueChange}
      onBlur={handleSetVisited}
    />
  );
};
